import { ApplicationType } from "@justhome/api-core";
import type { Locale } from "next/router";
import { DEFAULT_LOCALE, WEBSITE_HOST } from "./constants";
import { Page, PageEntityResponse } from "./generated/graphql";
import addUtmQueryToHref from "./utm/addUtmQueryToHref";

const PATHS = {
  en: {
    learningCenter: "/learning-center",
    article: (slug: string) => `/learning-center/article/${slug}`,
    category: (slug: string) => `/learning-center/${slug}`,
    compare: "/compare",
    budgetCheck: (stepId: string) => `/compare/budget-check/${stepId}`,
    partnerBudgetCheck: (partnerId: string, stepId: string) =>
      `/partner/${partnerId}/compare/budget-check/${stepId}`,
    partnerSearchProfileBudgetCheck: (partnerId: string, stepId: string) =>
      `/partner/${partnerId}/compare/search-profile/${stepId}`,
    partnerMeeting: (partnerId: string, campaign?: string) => {
      const params = new URLSearchParams();
      if (campaign) {
        params.set("utm_campaign", campaign);
      }

      return `/partner/${partnerId}/meeting?${params.toString()}`;
    },
    careers: "/careers",
    career: (slug: string) => `/careers/${slug}`,
    meeting: (meetingType?: string, campaign?: string) => {
      const params = new URLSearchParams();

      if (meetingType) {
        params.set("meetingType", meetingType);
      }
      if (campaign) {
        params.set("utm_campaign", campaign);
      }

      return `/meeting?${params.toString()}`;
    },
    medalFlow: (partnerId: string, clientMode: string, stepId: string) =>
      `/partner/${partnerId}/compare/${clientMode.toLowerCase()}/financing/${stepId}`,
  },
  de: {
    learningCenter: "/learning-center",
    article: (slug: string) => `/learning-center/article/${slug}`,
    category: (slug: string) => `/learning-center/${slug}`,
    compare: "/vergleich",
    budgetCheck: (stepId: string) => `/vergleich/budget-check/${stepId}`,
    partnerBudgetCheck: (partnerId: string, stepId: string) =>
      `/partner/${partnerId}/vergleich/budget-check/${stepId}`,
    partnerSearchProfileBudgetCheck: (partnerId: string, stepId: string) =>
      `/partner/${partnerId}/vergleich/search-profile/${stepId}`,
    partnerMeeting: (partnerId: string, campaign?: string) =>
      `/partner/${partnerId}/meeting?utm_campaign=${campaign ?? "justhome_website"}`,
    careers: "/karriere",
    career: (slug: string) => `/karriere/${slug}`,
    meeting: (meetingType?: string, campaign?: string) =>
      `/meeting?meetingType=${meetingType ?? ""}&utm_campaign=${campaign ?? "justhome_website"}`,
    medalFlow: (partnerId: string, clientMode: string, stepId: string) =>
      `/partner/${partnerId}/vergleich/${clientMode.toLowerCase()}/financing/${stepId}`,
  },
} as const;

export const APPLICATION_TYPE_PATHS: Record<
  string,
  Partial<Record<ApplicationType, string>>
> = {
  en: {
    BUILD_HOUSE: "build-house",
    BUY_WITH_SAVINGS: "buy-with-savings",
    REFINANCING: "refinancing",
  },
  de: {
    BUILD_HOUSE: "haus-bauen",
    BUY_WITH_SAVINGS: "immobilie-kaufen",
    REFINANCING: "anschlussfinanzieren",
  },
  immo: {
    BUILD_HOUSE: "haus-bauen",
    BUY_WITH_SAVINGS: "immobilie-kaufen",
    REFINANCING: "anschlussfinanzieren",
  },
};

const getPath = (locale: string) => PATHS[locale] ?? PATHS[DEFAULT_LOCALE];

export const getCanonicalLink = (locale: Locale, path: string): string => {
  const localePrefix = getLocalePrefix(locale);
  return `${WEBSITE_HOST}${localePrefix}${path === "/" ? "" : path}`;
};

export interface LanguageAlternate {
  hrefLang: string;
  href: string;
}

/**
 * The canonical an editor may have typed into the CMS, but only when it stays on this site:
 * the field still holds URLs from the old justhome.co domain that now 404.
 */
export const toSiteCanonical = (
  locale: Locale,
  path: string,
  canonicalURL?: string | null,
): string => {
  // Compared as an origin, not as a prefix: `https://justhome.com.example.org` starts with
  // the host string but is somebody else's domain.
  const isOwnOrigin = (() => {
    try {
      return new URL(canonicalURL!).origin === WEBSITE_HOST;
    } catch {
      return false;
    }
  })();

  return isOwnOrigin ? canonicalURL! : getCanonicalLink(locale, path);
};

export const getLanguageAlternates = ({
  locale,
  pageUrl,
  canonicalURL,
  alternateLanguageHref,
}: {
  locale: string;
  pageUrl: string;
  canonicalURL?: string | null;
  alternateLanguageHref?: string | null;
}): LanguageAlternate[] => {
  // A page that points its canonical at a different URL is not the one search engines should
  // index, so it has no business naming translations of itself.
  if (canonicalURL !== pageUrl || !alternateLanguageHref || locale === "immo") {
    return [];
  }

  const alternate = `${WEBSITE_HOST}${alternateLanguageHref === "/" ? "" : alternateLanguageHref}`;
  const [de, en] =
    locale === "en" ? [alternate, pageUrl] : [pageUrl, alternate];

  return [
    { hrefLang: "de", href: de },
    { hrefLang: "en", href: en },
    { hrefLang: "x-default", href: de },
  ];
};

export const getLocalePrefix = (locale: Locale) => {
  return locale === DEFAULT_LOCALE ? "" : `/${locale}`;
};

export const getApplicationProcessPath = (
  locale: string,
  subPath?: string,
  query?: Record<string, string>,
  partnerSlug?: string,
): string => {
  let path = getComparePath(locale);
  if (partnerSlug) {
    path = `/partner/${partnerSlug}${path}`;
  }
  if (subPath) {
    path = `${path}/${subPath}`;
  }
  return addUtmQueryToHref(path, query);
};

export const slugify = (text: string) => {
  return encodeURIComponent(
    text
      .replace(/[^\w ]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .toLowerCase(),
  );
};

export const getPageEntityPath = (page?: PageEntityResponse | null) => {
  return getPagePath(page?.data?.attributes);
};

export const getPagePath = (
  page?: Page | null,
  queryParams?: string | null,
): string | null => {
  if (!page?.slug) {
    return null;
  }

  if (page.slug === "/") {
    return "/";
  }
  if (queryParams) {
    return `/${page.slug}?${queryParams}`;
  }
  return `/${page.slug}`;
};

export const getLearningCenterPath = (locale: string) =>
  getPath(locale).learningCenter;

export const getArticlePath = (locale: string, slug: string) =>
  getPath(locale).article(slug);

export const getCategoryPath = (locale: string, slug: string) =>
  getPath(locale).category(slug);

export const getCareerPath = (locale: string, slug: string) =>
  getPath(locale).career(slug);

type Localizations = {
  data?: Array<{
    attributes?: {
      contentSections?: unknown[] | null;
      locale?: string | null;
      slug?: string | null;
    } | null;
  } | null> | null;
};

export const getAlternateLanguageHref = (
  language: string,
  localizations: Localizations | null | undefined,
  contentType: "page" | "article" | "career",
  pathPrefix = "",
): string | null => {
  const locale = language === "en" ? "de" : "en";
  const localizedContent = localizations?.data?.find(
    (entry) => entry?.attributes?.locale === locale,
  )?.attributes;

  if (!localizedContent?.slug) {
    return null;
  }

  if (contentType === "article" && !localizedContent.contentSections?.length) {
    return null;
  }

  const path =
    contentType === "article"
      ? getArticlePath(locale, localizedContent.slug)
      : contentType === "career"
        ? getCareerPath(locale, localizedContent.slug)
        : localizedContent.slug === "/"
          ? "/"
          : `/${localizedContent.slug}`;

  return (
    `${getLocalePrefix(locale)}${pathPrefix}${path === "/" ? "" : path}` || "/"
  );
};

export const appendHrefSuffix = (
  href: string | null,
  suffix: string,
): string | null => (href ? `${href}${suffix}` : null);

export const getComparePath = (locale: string) => getPath(locale).compare;

export const getCareersPath = (locale: string) => getPath(locale).careers;

export const getMeetingPath = (locale: string, meetingType?: string) =>
  getPath(locale).meeting(meetingType);

export const getBudgetCheckPath = (locale: string, stepId: string) =>
  getPath(locale).budgetCheck(stepId);

export const getPartnerBudgetCheckPath = (
  locale: string,
  partnerId: string,
  stepId: string,
) => getPath(locale).partnerBudgetCheck(partnerId, stepId);

export const getPartnerSearchProfileBudgetCheckPath = (
  locale: string,
  partnerId: string,
  stepId: string,
) => getPath(locale).partnerSearchProfileBudgetCheck(partnerId, stepId);

export const getPartnerMeetingPath = (
  locale: string,
  partnerId: string,
  campaign?: string,
) => getPath(locale).partnerMeeting(partnerId, campaign);

export const getMedalFlowPath = (
  locale: string,
  partnerId: string,
  clientMode: string,
  stepId: string,
) => getPath(locale).medalFlow(partnerId, clientMode, stepId);

export const replaceLastRouteParam = (route: string, param: string) => {
  const segments = route.split("/").filter((s) => s);
  segments[segments.length - 1] = param;
  return "/" + segments.join("/");
};

export const applicationTypeToPath = (
  locale: string,
  applicationType: ApplicationType | undefined,
) => {
  if (!applicationType) {
    return "";
  }
  return "short/" + (APPLICATION_TYPE_PATHS[locale]?.[applicationType] || "");
};

export const pathToApplicationType = (path: string) => {
  return Object.values(APPLICATION_TYPE_PATHS)
    .map((a) => Object.entries(a))
    .flat()
    .find(([_, p]) => p === path)?.[0];
};
